/**
 * EngineeringHealthTile — super-admin dashboard tile that surfaces
 * the LOC budget audit (`scripts/audit_loc_budget.py`) directly into
 * the UI so engineering health is visible without digging through CI
 * logs.
 *
 * Pairs with `routes/code_health.py::get_loc_budget`. Tracks two
 * tiers:
 *   - Routers shrunk by a refactor (hard ceiling).
 *   - Mature services modules (current LOC + ~100 headroom).
 *
 * Headroom semantics (sorted ascending):
 *   - over_budget: red
 *   - 0–25 lines headroom: amber (close to wall)
 *   - 26+ lines headroom: emerald
 *
 * Files over the 500 LOC `split_signal_loc` threshold get a small
 * "split candidate" pill — informational only, not a failure.
 *
 * Empty / forbidden paths follow the SalesIntelTile / Whitepaper
 * tile conventions: return null on 403, render an explicit error
 * line otherwise. No interactive controls — this is a read-only
 * status panel.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Activity, AlertTriangle, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


function _statusClass(file) {
  if (file.over_budget) return 'text-rose-700 bg-rose-50 border-rose-200';
  if (file.headroom <= 25) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-emerald-700 bg-emerald-50 border-emerald-200';
}


/**
 * OversizedTrendStrip — renders the top-5 oversized files this
 * week with delta vs the closest run from ≥7 days ago. Lives at
 * the bottom of the tile so the existing LOC budget list (the
 * pinned/refactored slice) stays the headline. Self-renders
 * "no baseline yet" copy when the platform hasn't accumulated a
 * week of history — common on fresh installs.
 */
function OversizedTrendStrip() {
  const { data, loading, error } = useTelemetryTile({
    url: '/admin/code-health/oversized-trend',
    deps: [],
  });

  if (error === 'forbidden') return null;
  if (loading) return null;
  if (!data || (data.top_files || []).length === 0) return null;

  const baselineMissing = !data.baseline_at;
  const STATUS = {
    new: { Icon: Sparkles, cls: 'text-violet-700 bg-violet-50 border-violet-200', label: 'NEW' },
    up: { Icon: TrendingUp, cls: 'text-rose-700 bg-rose-50 border-rose-200', label: '↑' },
    down: { Icon: TrendingDown, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: '↓' },
    flat: { Icon: Minus, cls: 'text-slate-600 bg-slate-50 border-slate-200', label: '=' },
  };

  return (
    <div
      className="mt-4 pt-4 border-t border-indigo-100"
      data-testid="oversized-trend-strip"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
          Top oversized this week
        </p>
        <span className="text-[10.5px] text-slate-500" data-testid="oversized-trend-summary">
          {baselineMissing
            ? 'no baseline yet · need 7d of history'
            : `${data.newly_oversized} new · ${data.still_oversized} still over`}
        </span>
      </div>
      <ol className="space-y-1" data-testid="oversized-trend-list">
        {data.top_files.map((f, i) => {
          const tone = STATUS[f.status] || STATUS.flat;
          const { Icon } = tone;
          const deltaText = f.delta == null
            ? (baselineMissing ? '—' : 'NEW')
            : (f.delta > 0 ? `+${f.delta}` : `${f.delta}`);
          return (
            <li
              key={f.file}
              data-testid={`oversized-trend-row-${i}`}
              className={`flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md border bg-white text-[12px] ${tone.cls}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Icon className="h-3 w-3 shrink-0" />
                <span className="font-mono text-[11.5px] text-slate-800 truncate">{f.file}</span>
              </div>
              <div className="text-right shrink-0 tabular-nums">
                <span className="font-bold">{f.lines}</span>
                <span className="text-[10.5px] ml-2 font-medium">{deltaText}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}


export default function EngineeringHealthTile() {
  const { data, loading, error } = useTelemetryTile({
    url: '/admin/code-health/loc-budget',
    deps: [],
  });

  if (error === 'forbidden') return null;

  const files = data?.files || [];
  const totals = data?.totals || { checked: 0, over_budget: 0, split_candidates: 0 };
  const splitSignal = data?.split_signal_loc ?? 500;
  const quarter = data?.quarter || null;

  return (
    <Card
      data-testid="engineering-health-tile"
      className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" />
              Engineering Health · LOC Budgets
            </CardTitle>
            <CardDescription className="mt-0.5">
              Routers + services pinned by past refactors. Same data the CI
              gate uses — files trending toward their ceiling are visible
              here before the build breaks.
            </CardDescription>
          </div>
          {totals.over_budget > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700 border border-rose-200"
              data-testid="loc-budget-over-budget-pill"
            >
              <AlertTriangle className="h-3 w-3" />
              {totals.over_budget} over budget
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="loc-budget-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="loc-budget-error">
            Couldn't load LOC budget status.
          </p>
        ) : files.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="loc-budget-empty">
            No files tracked yet. Add entries to{' '}
            <code className="text-[11px] bg-slate-100 px-1 rounded">scripts/audit_loc_budget.py</code>.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3 text-[11px] text-slate-500 flex-wrap">
              <span data-testid="loc-budget-checked-count">
                <strong className="text-slate-700">{totals.checked}</strong> tracked
              </span>
              <span aria-hidden="true">·</span>
              <span data-testid="loc-budget-split-count">
                <strong className="text-slate-700">{totals.split_candidates}</strong> over {splitSignal} LOC
              </span>
              {quarter && quarter.graduations > 0 && (
                <>
                  <span aria-hidden="true">·</span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10.5px] font-semibold"
                    data-testid="loc-budget-quarter-trophy"
                    title={`Modules that dropped below ${splitSignal} LOC during ${quarter.label}`}
                  >
                    🏆 {quarter.graduations} graduated · {quarter.label}
                  </span>
                </>
              )}
            </div>
            <ol className="space-y-1.5" data-testid="loc-budget-list">
              {files.map((f, i) => (
                <li
                  key={f.path}
                  className={
                    'flex items-start justify-between gap-3 px-2.5 py-1.5 rounded-md border bg-white text-[12px] ' +
                    _statusClass(f)
                  }
                  data-testid={`loc-budget-row-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11.5px] text-slate-800 truncate">
                      {f.path}
                    </p>
                    <p className="text-[10.5px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <span className="uppercase tracking-wide font-semibold text-slate-400">{f.tier}</span>
                      {f.split_candidate && (
                        <span
                          className="inline-block px-1.5 rounded-sm bg-violet-100 text-violet-700 text-[10px] font-medium"
                          data-testid={`loc-budget-split-pill-${i}`}
                        >
                          split candidate
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0 tabular-nums">
                    <p className="font-bold leading-tight">{f.loc} <span className="text-slate-400 font-normal">/ {f.budget}</span></p>
                    <p className="text-[10.5px] mt-0.5">
                      {f.over_budget ? 'OVER' : `+${f.headroom} headroom`}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <OversizedTrendStrip />
          </>
        )}
      </CardContent>
    </Card>
  );
}
